import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  ChevronRight,
  Shield,
  Globe,
  Headphones,
  Facebook,
  Instagram,
} from 'lucide-react';
import '../styles/footer-exact.css';
import {
  COMPANY_LOGO_SRC,
  WHATSAPP_DISPLAY,
  buildWhatsAppUrl,
  defaultWhatsAppQuoteMessage,
} from '../constants/branding';
import API_BASE from '../services/apiBase';
import { useToast } from '../features/toast/ToastContext';

const SOCIAL = [
  { label: 'Facebook', href: 'https://www.facebook.com/share/1BGRbKoUMU/', Icon: Facebook },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/jet_shipping_cargo_malaysia?igsh=MTJpazF0ZXdpZnlnYQ==',
    Icon: Instagram,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@jetshippingcargomalaysia?_r=1&_t=ZS-960nHoICtnO',
    Icon: null,
  },
];

const TiktokGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const WhatsAppGlyph = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ADDRESS_LINES = [
  'No.AZ-B1-07, Lot No.1B/2(A), Anzen Business Park',
  'Jalan 4/37A, Kawasan Industri Taman Bukit Maluri',
  'Kepong, Kuala Lumpur, 52100 Malaysia',
];

const Chev = () => <ChevronRight className="chev" size={14} aria-hidden />;

const Footer = () => {
  const location = useLocation();
  const { success: toastSuccess, error: toastError } = useToast();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterBusy, setNewsletterBusy] = useState(false);

  const waHref = useMemo(
    () => buildWhatsAppUrl(defaultWhatsAppQuoteMessage(location.pathname)),
    [location.pathname],
  );

  const handleNewsletter = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email) {
      toastError('Enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toastError('Please enter a valid email address');
      return;
    }
    setNewsletterBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Could not subscribe');
      setNewsletterEmail('');
      toastSuccess(json.message || 'Subscribed successfully!');
    } catch (err) {
      toastError(err.message || 'Subscription failed');
    } finally {
      setNewsletterBusy(false);
    }
  };

  return (
    <>
    <footer className="jet-footer">
      <div className="jet-footer__top">
        <div>
          <div className="jet-footer__brand">
            <img src={COMPANY_LOGO_SRC} alt="Jet Shipping Cargo" className="jet-footer__brand-logo" decoding="async" />
            <div className="jet-footer__brand-text">
              <span className="jet-footer__brand-name">JET SHIPPING CARGO</span>
              <span className="jet-footer__brand-tag">Global Cargo Solutions</span>
            </div>
          </div>
          <p className="jet-footer__blurb">
            Air, sea, and land freight with transparent milestones and responsive coordination — built for teams that
            need cargo moving on schedule.
          </p>
          <ul className="jet-footer__contact-list">
            <li>
              <span className="jet-footer__contact-icon">
                <MapPin size={16} aria-hidden />
              </span>
              <span>
                {ADDRESS_LINES.map((line) => (
                  <React.Fragment key={line}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </span>
            </li>
            <li>
              <span className="jet-footer__contact-icon">
                <Phone size={16} aria-hidden />
              </span>
              <span>
                <a href={waHref} target="_blank" rel="noopener noreferrer">
                  {WHATSAPP_DISPLAY} (WhatsApp)
                </a>
                <br />
                +60 11-6053 6205
              </span>
            </li>
            <li>
              <span className="jet-footer__contact-icon">
                <Mail size={16} aria-hidden />
              </span>
              <span>
                jetshippingandlogistic@gmail.com
                <br />
                support@jetshippingandlogistic.com
              </span>
            </li>
            <li>
              <span className="jet-footer__contact-icon">
                <Clock size={16} aria-hidden />
              </span>
              <span>
                Mon–Fri: 8:00 AM – 6:00 PM
                <br />
                Sat: 9:00 AM – 4:00 PM · Sun: Closed
              </span>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="jet-footer__col-title">Quick Links</h2>
          <ul className="jet-footer__linklist">
            {[
              ['Home', '/'],
              ['About Us', '/about'],
              ['Our Services', '/services'],
              ['Track Shipment', '/tracking'],
              ['Sri Lanka customs & baggage', '/sri-lanka-customs-baggage'],
              ['News & Updates', '/news'],
              ['Contact Us', '/contact'],
              ['Leave a Review', '/review'],
              ['FAQs', '/faqs'],
            ].map(([label, to]) => (
              <li key={label}>
                <Link to={to}>
                  {label}
                  <span className="chev">
                    <Chev />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="jet-footer__col-title">Our Services</h2>
          <ul className="jet-footer__linklist">
            {[
              ['Air Freight', '/services'],
              ['Sea Freight', '/services'],
              ['Land Transport', '/services'],
              ['Warehousing', '/services'],
              ['Door to Door Delivery', '/services'],
              ['Customs Clearance', '/services'],
              ['CBM Calculator', '/cbm-calculator'],
              ['Supply Chain Solutions', '/services'],
            ].map(([label, to]) => (
              <li key={label}>
                <Link to={to}>
                  {label}
                  <span className="chev">
                    <Chev />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="jet-footer__support-block">
            <h2 className="jet-footer__col-title">Support</h2>
            <ul className="jet-footer__linklist">
              {[
                ['Help Center', '/help'],
                ['Shipping Guide', '/services'],
                ['Terms & Conditions', '/terms'],
                ['Privacy Policy', '/privacy'],
                ['Refund Policy', '/refund-policy'],
                ['Careers', '/careers'],
              ].map(([label, to]) => (
                <li key={label}>
                  <Link to={to}>
                    {label}
                    <span className="chev">
                      <Chev />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <h2 className="jet-footer__col-title">Newsletter</h2>
          <div className="jet-footer__newsletter">
            <p>Subscribe for logistics tips, lane updates, and company news.</p>
            <form className="jet-footer__newsletter-form" onSubmit={handleNewsletter}>
              <input
                type="email"
                name="footer-newsletter-email"
                autoComplete="email"
                placeholder="Enter your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={newsletterBusy}
              />
              <button type="submit" aria-label="Subscribe" disabled={newsletterBusy}>
                <Send size={18} />
              </button>
            </form>
          </div>
          <p className="jet-footer__social-title">Follow Us</p>
          <div className="jet-footer__social">
            {SOCIAL.map(({ label, href, Icon }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                {Icon ? <Icon size={18} /> : <TiktokGlyph size={18} />}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="jet-footer__values">
        <div className="jet-footer__value">
          <span className="jet-footer__value-icon">
            <Shield size={22} aria-hidden />
          </span>
          <div>
            <strong>Safe &amp; Secure</strong>
            <span>Careful handling and compliance-aware coordination.</span>
          </div>
        </div>
        <div className="jet-footer__value">
          <span className="jet-footer__value-icon">
            <Globe size={22} aria-hidden />
          </span>
          <div>
            <strong>Global Network</strong>
            <span>Air, sea, and land across key trade corridors.</span>
          </div>
        </div>
        <div className="jet-footer__value">
          <span className="jet-footer__value-icon">
            <Clock size={22} aria-hidden />
          </span>
          <div>
            <strong>On-Time Delivery</strong>
            <span>Milestones and updates you can plan around.</span>
          </div>
        </div>
        <div className="jet-footer__value">
          <span className="jet-footer__value-icon">
            <Headphones size={22} aria-hidden />
          </span>
          <div>
            <strong>24/7 Support</strong>
            <span>Dedicated help when lanes or timelines change.</span>
          </div>
        </div>
      </div>

      <div className="jet-footer__bottombar">
        <span>&copy; {new Date().getFullYear()} Jet Shipping Cargo. All rights reserved.</span>
        <div className="jet-footer__bottom-links">
          <Link to="/sitemap">Sitemap</Link>
          <span>|</span>
          <Link to="/terms">Terms &amp; Conditions</Link>
          <span>|</span>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
      </div>
    </footer>
    <a
      className="jet-whatsapp-fab"
      href={waHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Chat on WhatsApp ${WHATSAPP_DISPLAY}`}
      title={`WhatsApp ${WHATSAPP_DISPLAY}`}
    >
      <WhatsAppGlyph size={30} />
    </a>
    </>
  );
};

export default Footer;
